import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'ramda';
import { useForm, useFieldArray, FormProvider } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { Redirect } from 'react-router-dom';
import { useApolloClient, gql } from '@apollo/client';
import { useMeals } from 'utils/hooks/useMeals';
import moment from 'moment';
import { ChevronRight } from 'react-feather';

import { bookingShape, passengersShape, customerShape, historyShape } from 'constants/propTypes';
import { parsePhoneNumber, formatPhoneNumber } from 'utils/parsePhoneNumber';
import { renameKeys, calcSegmentPointsTotal } from 'utils/functions';
import { hasGroupDetailsForm } from 'utils/isGroupBooking';
import { getLoyaltyData } from 'utils/loyaltyData';
import { correctlyFormatDate } from 'utils/dates';
import buildJourneyDigestShape from 'utils/buildJourneyDigestShape';
import { GET_MEMBERSHIP_DETAILS } from 'store/queries';
import { LOYALTY_NUMBER_PREFIX } from 'constants/passengers';
import { nonCancelledExtras, totalExtras } from 'utils/payment';
import { DEFAULT_LANGUAGE, LANGUAGE_KEY } from 'constants/booking';
import { Row } from 'components/shared/Layout';
import JourneyDigest from 'components/shared/JourneyDigest';
import SeatSelection from 'components/shared/SeatSelection';
import RadioFieldSet from 'components/ui/RadioFieldSet';
import MealChangeWarning from 'components/shared/MealChangeWarning';
import discountCardsMap from 'types/discountCardsMap';
import { customMealCode } from 'types/meals';
import { nonCancelledEurostarAndGreenspeedLegs, isEROnly, hasDog } from 'utils/booking';
import { brandCode } from 'constants/brandCodes';

import { getTotalAddonPrice } from '../JourneySearch/JourneySearch.utils';

import { ConfirmButton, WarningContainer } from './BookingPathPassengerDetails.styles';
import PassengerDetailsForm from './PassengerDetailsForm';
import updateBookingLanguage from 'utils/updateBookingLanguage';

import { isNoMealRoute, somePassengersEntitledToMeal } from 'utils/meals';
import AlertBox from 'components/ui/AlertBox';
import { isEurostarOffer } from 'utils/isEurostarOffer';
import { hasSNCBLeg, hasPotentialSNCBStationWithoutSNCF } from 'utils/segmentTypes';
import { isFakeName } from 'utils/isFakeName';
import { prefixPhoneNumberWith00 } from 'utils/prefixPhoneNumberTo00';
import { ELIGIBLE_LAP_PASSENGER_TYPES } from 'constants/passengers';
import InfantDetailsForm from './InfantDetailsForm';

const BRANDS_WITH_FAKE_NAMES = [brandCode.SNCB, brandCode.NS];

const BookingPathPassengerDetails = ({
  bookingData,
  client,
  passengerDetails,
  updatePassengers,
  customerInfo,
  canAddSpecialAssistanceToBooking = false,
  isAssistanceSelected,
  onIsAssistanceSelected,
  loading,
  history,
  handleCompletedMutation,
  infantsOnLapEnabled,
  infantCount,
  addedParentIds,
  existingInfants = []
}) => {
  const subscriptionCode = new URLSearchParams(history.location.search).get('subscriptionCode');
  const subscriptionID = new URLSearchParams(history.location.search).get('subscriptionID');
  const apolloClient = useApolloClient();
  const meals = useMeals({
    booking: bookingData
  });

  const showDOB = bookingData.isDOBRequired;
  const isHotelOnly = history.location.state?.isHotelOnly;

  const {
    reference,
    productFamilyGroup,
    currency,
    passengers,
    outbound,
    outboundTotalPrice,
    inbound,
    inboundTotalPrice,
    totalPrice,
    provisionalOfflineFees = [],
    outboundOrigin,
    outboundDestination,
    outboundExtras,
    inboundExtras,
    additionalDetails,
    isLoyalty
  } = bookingData;

  const isSNCBBooking = hasSNCBLeg([...outbound, ...inbound]) || hasPotentialSNCBStationWithoutSNCF({ outbound, inbound });
  const eligiblePassengers = passengers.filter((p) => ELIGIBLE_LAP_PASSENGER_TYPES.includes(p.type));
  const defaultLanguage = additionalDetails?.find(({ key }) => key === LANGUAGE_KEY)?.value || DEFAULT_LANGUAGE;
  const [bookingLanguage, setBookingLanguage] = useState(defaultLanguage);

  const nameFormatter = (passengers) =>
    passengers.map((passenger) => ({
      ...passenger,
      firstName: passenger.firstName.trim(),
      lastName: passenger.lastName.trim()
    }));

  const defaultValues = {
    passengers: nameFormatter(passengerDetails),
    infants: Array.from({ length: infantsOnLapEnabled ? infantCount : 0 }, (_, i) => {
      const parentId = eligiblePassengers[i]?.id || '';
      const existing = existingInfants.find((inf) => inf.passengerRef === parentId);
      return {
        firstName: existing?.infant_first_name || '',
        lastName: existing?.infant_last_name || '',
        parentPassengerId: parentId
      };
    })
  };

  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues
  });

  const { register, handleSubmit, control, formState, setValue, getValues, setError, trigger } = formMethods;
  const { errors } = formState;

  const { fields } = useFieldArray({ control, name: 'passengers' });

  useEffect(() => {
    const hasFakeNameLeg = [...outbound, ...inbound].some(({ serviceType }) => BRANDS_WITH_FAKE_NAMES.includes(serviceType?.brandCode));

    if (hasFakeNameLeg) {
      const { passengers: passengersFromState } = getValues();
      passengersFromState.forEach((pax, i) => {
        const firstName = pax?.firstName;
        const lastName = pax?.lastName;

        if (isFakeName({ firstName, lastName })) {
          setValue(`passengers.${i}.firstName`, '');
          setValue(`passengers.${i}.lastName`, '');
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmitLoyaltyNumber = async (index, loyaltyNumber) => {
    try {
      const { data, errors: submitLoyaltyNumber } = await apolloClient.query({
        query: GET_MEMBERSHIP_DETAILS,
        variables: {
          membershipNumber: `${LOYALTY_NUMBER_PREFIX}${loyaltyNumber}`
        }
      });
      const customerDetails = data.getCustomerDetails || data.getCustomerInfo;
      if (customerDetails && !submitLoyaltyNumber?.length) {
        if (customerDetails?.firstName) {
          setValue(`passengers.${index}.firstName`, customerDetails.firstName, {
            shouldValidate: true
          });
        }
        if (customerDetails?.lastName) {
          setValue(`passengers.${index}.lastName`, customerDetails.lastName, {
            shouldValidate: true
          });
        }
        if (customerDetails?.emailAddress) {
          setValue(`passengers.${index}.email`, customerDetails.emailAddress, {
            shouldValidate: true
          });
        }
        if (customerDetails?.profile?.dateOfBirth) {
          setValue(`passengers.${index}.birthDate`, customerDetails.profile.dateOfBirth, {
            shouldValidate: true
          });
        }
        if (customerDetails?.phoneNumber) {
          const { countryCode, number } = parsePhoneNumber(customerDetails.phoneNumber);

          if (countryCode) {
            setValue(`passengers.${index}.phoneCountryCode`, countryCode.replace('+', ''), {
              shouldValidate: true
            });
          }

          if (number) {
            setValue(`passengers.${index}.phoneNumber`, number, {
              shouldValidate: true
            });
          }
        }
      } else {
        setError(`passengers.${index}.loyaltyNumber`, { type: 'validate', message: 'paxForm.validator.loyaltyAccountNotFound' });
      }
    } catch (e) {
      setError(`passengers.${index}.loyaltyNumber`, { type: 'validate', message: 'paxForm.validator.loyaltyAccountNotFound' });
    }
  };

  const onSubmit =
    (updatePassengerSeats = () => null) =>
    async (values) => {
      try {
        const formattedValues = { ...values, passengers: nameFormatter(values.passengers) };
        const newPassengerDetails = formattedValues.passengers
          .map((newValues, index) => ({
            ...passengerDetails[index],
            ...newValues
          }))
          .map(renameKeys({ id: 'paxId' }));

        const loyaltyChecks = await Promise.all(
          newPassengerDetails.map(async (passenger, index) => {
            try {
              if (passenger.loyaltyNumber) {
                let hasError = false;

                const { data } = await apolloClient.query({
                  query: GET_MEMBERSHIP_DETAILS,
                  variables: {
                    membershipNumber: `${LOYALTY_NUMBER_PREFIX}${passenger.loyaltyNumber}`
                  }
                });
                const customerDetails = data.getCustomerDetails || data.getCustomerInfo;

                if (passenger?.firstName?.toUpperCase() !== customerDetails?.firstName?.toUpperCase()) {
                  setError(`passengers.${index}.firstName`, { type: 'validate', message: 'paxForm.validator.incorrectFirstName' });

                  hasError = true;
                }

                if (passenger?.lastName?.toUpperCase() !== customerDetails?.lastName?.toUpperCase()) {
                  setError(`passengers.${index}.lastName`, { type: 'validate', message: 'paxForm.validator.incorrectLastName' });

                  hasError = true;
                }

                return !hasError;
              }

              return true;
            } catch {
              setError(`passengers.${index}.loyaltyNumber`, { type: 'validate', message: 'paxForm.validator.notExistLoyaltyNumber' });

              return false;
            }
          })
        );

        if (loyaltyChecks.some((value) => value === false)) {
          return null;
        }

        const duplicatePaxNames = newPassengerDetails.filter(
          (p, index) =>
            !!newPassengerDetails.find(
              (p2, index2) =>
                p2.firstName.trim().toLowerCase() === p.firstName.trim().toLowerCase() &&
                p2.lastName.trim().toLowerCase() === p.lastName.trim().toLowerCase() &&
                index !== index2
            )
        );

        if (isSNCBBooking && duplicatePaxNames.length > 0) {
          for (const pax of duplicatePaxNames) {
            let index = newPassengerDetails.findIndex((p) => p.paxId === pax.paxId);

            if (index === -1) {
              continue;
            }

            setError(`passengers.${index}.firstName`, { type: 'validate', message: 'paxForm.validator.duplicatePaxNames' });
          }

          return null;
        }

        const customer = customerInfo
          ? {
              firstName: customerInfo.firstName,
              lastName: customerInfo.lastName,
              email: customerInfo.emailAddress,
              ...(customerInfo.phoneNumber && {
                phoneNumber: formatPhoneNumber(parsePhoneNumber(customerInfo.phoneNumber)) || customerInfo.phoneNumber.replace(/^\+/, '')
              }),
              address: {
                ...(customerInfo.address?.street && { street: customerInfo.address?.street }),
                ...(customerInfo.address?.postalCode && { postalCode: customerInfo.address?.postalCode }),
                ...(customerInfo.address?.city && { city: customerInfo.address?.city }),
                ...(customerInfo.address?.countryCode && { countryCode: customerInfo.address?.countryCode })
              }
            }
          : {
              firstName: newPassengerDetails[0].firstName,
              lastName: newPassengerDetails[0].lastName,
              email: newPassengerDetails[0].email,
              phoneNumber:
                newPassengerDetails[0].phoneCountryCode &&
                newPassengerDetails[0].phoneNumber &&
                `${newPassengerDetails[0].phoneCountryCode}${newPassengerDetails[0].phoneNumber.replace(/^0+/, '')}`
            };

        client.writeQuery({
          query: gql`
            query {
              passengerDetails @client
            }
          `,
          data: {
            passengerDetails: newPassengerDetails
          }
        });

        if (!isHotelOnly) {
          await updatePassengerSeats();
        }

        const discountCards = [];

        if (discountCardsMap[productFamilyGroup]) {
          discountCards.push(discountCardsMap[productFamilyGroup]);
        }

        await meals.saveMeals();

        await updatePassengers({
          variables: {
            bookingReference: reference,
            customer: {
              ...customer,
              ...(customer.phoneNumber && { phoneNumber: prefixPhoneNumberWith00({ phoneNumber: customer.phoneNumber }) })
            },
            passengers: newPassengerDetails.map(
              ({ firstName, lastName, birthDate, email, phoneCountryCode, phoneNumber, loyaltyNumber, paxId }) => ({
                id: paxId,
                firstName,
                lastName,
                email,
                discountCards: getLoyaltyData(loyaltyNumber, paxId, passengerDetails, subscriptionCode, subscriptionID).concat(
                  discountCards
                ),
                ...(birthDate && !isSNCBBooking && { birthDate: correctlyFormatDate(birthDate) }),
                phoneNumber: `00${phoneCountryCode}${phoneNumber.replace(/^0+/, '')}`
              })
            )
          }
        });

        await updateBookingLanguage(apolloClient, additionalDetails, reference, bookingLanguage);

        await handleCompletedMutation(values.infants || []);
      } catch (error) {
        return error;
      }
    };

  const hasError = ({ name, index }) => Boolean(errors && errors.passengers && errors.passengers[index] && errors.passengers[index][name]);

  const getError = ({ name, index }) => errors?.passengers?.[index]?.[name]?.message || '';

  const copyLastNameToAllPassengers = () => {
    const { passengers: passengersFromState, infants: infantsFromState = [] } = getValues();
    const firstPassenger = passengersFromState[0];
    const firstPassengerLastName = firstPassenger && firstPassenger.lastName;
    passengersFromState.forEach((_, i) => {
      setValue(`passengers.${i}.lastName`, firstPassengerLastName, {
        shouldValidate: true
      });
    });
    infantsFromState.forEach((infant, i) => {
      if (!addedParentIds?.has(infant.parentPassengerId)) {
        setValue(`infants.${i}.lastName`, firstPassengerLastName, {
          shouldValidate: true
        });
      }
    });
  };

  const copyEmailAllPassengers = () => {
    const { passengers: passengersFromState } = getValues();
    const firstPassenger = passengersFromState[0];
    const firstPassengerEmail = firstPassenger && firstPassenger.email;
    passengersFromState.forEach((_, i) => {
      setValue(`passengers.${i}.email`, firstPassengerEmail, {
        shouldValidate: true
      });
    });
  };

  const copyPhoneNumberAllPassengers = () => {
    const { passengers: passengersFromState } = getValues();
    const firstPassenger = passengersFromState[0];
    const firstPassengerPhoneNumber = firstPassenger && firstPassenger.phoneNumber;
    const firstPassengerPhoneCountryCode = firstPassenger && firstPassenger.phoneCountryCode;
    passengersFromState.forEach((_, i) => {
      setValue(`passengers.${i}.phoneCountryCode`, firstPassengerPhoneCountryCode, {
        shouldValidate: true
      });
      setValue(`passengers.${i}.phoneNumber`, firstPassengerPhoneNumber, {
        shouldValidate: true
      });
    });
  };

  if (hasGroupDetailsForm(productFamilyGroup)) {
    return <Redirect to={{ pathname: `/booking/${reference}/group`, search: history.location?.search, state: history.location?.state }} />;
  }

  const offlineFee = provisionalOfflineFees[0];

  const outboundTotalPoints = calcSegmentPointsTotal(bookingData.outbound);
  const inboundTotalPoints = calcSegmentPointsTotal(bookingData.inbound);
  const nonCancelledOutboundExtras = nonCancelledExtras(outboundExtras);
  const nonCancelledInboundExtras = nonCancelledExtras(inboundExtras);
  const outboundTotalExtrasPrice = totalExtras(nonCancelledOutboundExtras);
  const inboundTotalExtrasPrice = totalExtras(nonCancelledInboundExtras);

  const bookingHasDog = hasDog(bookingData);
  const options = [
    { value: 'no', label: <FormattedMessage id="interaction.no" />, disabled: bookingHasDog },
    { value: 'yes', label: <FormattedMessage id="interaction.yes" />, disabled: bookingHasDog }
  ];

  const selectedAssistanceOption = isAssistanceSelected ? options[1].value : options[0].value;

  const { outboundTotalAddOnPrice = 0, inboundTotalAddOnPrice = 0 } = getTotalAddonPrice({
    outbound,
    inbound
  });

  const shouldBlockAddingAssistance =
    isAssistanceSelected &&
    !(canAddSpecialAssistanceToBooking && isEurostarOffer([...(bookingData?.outbound ?? []), ...(bookingData?.inbound ?? [])]));

  return (
    <SeatSelection
      bookingData={bookingData}
      outbound={nonCancelledEurostarAndGreenspeedLegs(bookingData.outbound)}
      inbound={nonCancelledEurostarAndGreenspeedLegs(bookingData.inbound)}
      isNewBooking
    >
      {({
        legId,
        renderedSeatSelection,
        isUpdatingSeats,
        disabledContinue,
        selectedSeats = [],
        selectedPassenger,
        selectedPassengers,
        journey,
        actions: { updatePassengerSeats, onSelectNewPassenger, onRemovePassengerSelection }
      }) => {
        const isConfirmButtonDisabled = !formState.isValid || formState.isSubmitting || isUpdatingSeats || disabledContinue;

        const bookingIsEROnly = isEROnly(bookingData);
        const entitledToMeal = somePassengersEntitledToMeal(passengers, selectedSeats);

        const displayMealWarning =
          !bookingIsEROnly &&
          entitledToMeal &&
          journey?.timing?.isoDate &&
          moment(journey.timing.isoDate).subtract(2, 'days').isBefore(moment()) &&
          meals.mealsToUpdate[journey.direction].filter(({ mealCode }) => mealCode !== customMealCode.REGULAR_MEAL).length > 0;

        return (
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit(updatePassengerSeats))}>
              <Row span={14} start={2}>
                {renderedSeatSelection && !isHotelOnly && (
                  <Row span={16}>
                    <Row span={16}>{renderedSeatSelection}</Row>
                  </Row>
                )}
                <PassengerDetailsForm
                  selectedSeats={selectedSeats}
                  selectedPassenger={selectedPassenger}
                  selectedPassengers={selectedPassengers}
                  onSelectNewPassenger={onSelectNewPassenger}
                  onRemovePassengerSelection={onRemovePassengerSelection}
                  register={register}
                  fields={fields}
                  hasError={hasError}
                  getError={getError}
                  showDOB={showDOB}
                  copyLastNameToAllPassengers={copyLastNameToAllPassengers}
                  copyEmailAllPassengers={copyEmailAllPassengers}
                  copyPhoneNumberAllPassengers={copyPhoneNumberAllPassengers}
                  passengerDetails={passengerDetails}
                  getValues={getValues}
                  trigger={trigger}
                  onSubmitLoyaltyNumber={onSubmitLoyaltyNumber}
                  isHotelOnly={isHotelOnly}
                  meals={meals.availableMeals[legId]}
                  mealsToUpdate={meals.mealsToUpdate}
                  direction={journey?.direction}
                  setMealsForPassenger={meals.setMealsForPassenger}
                  bookingLanguage={bookingLanguage}
                  setBookingLanguage={setBookingLanguage}
                  hasNoMealRoute={isNoMealRoute(journey?.origin?.uic, journey?.destination?.uic)}
                />
                {infantsOnLapEnabled && infantCount > 0 && (
                  <InfantDetailsForm
                    infantCount={infantCount}
                    eligiblePassengers={eligiblePassengers}
                    passengerCount={fields.length}
                    addedParentIds={addedParentIds}
                  />
                )}
              </Row>
              {displayMealWarning && (
                <WarningContainer>
                  <MealChangeWarning />
                </WarningContainer>
              )}
              <Row id="assistance-row">
                <h3>
                  <FormattedMessage id="bookingPath.contact.assistanceExtrasSelectionTitle" />
                </h3>
                <RadioFieldSet
                  noDefault
                  name="assistance-selection"
                  radioButtons={options}
                  selectedValue={selectedAssistanceOption}
                  onChange={onIsAssistanceSelected}
                />
              </Row>
              {isAssistanceSelected && (
                <WarningContainer data-cy="assistance-warning">
                  <AlertBox variant={AlertBox.variants.WARNING}>
                    <FormattedMessage id="addAssistance.warning" />
                  </AlertBox>
                </WarningContainer>
              )}

              <JourneyDigest
                outbound={buildJourneyDigestShape(outbound)}
                inbound={buildJourneyDigestShape(inbound)}
                outboundTotalPrice={outboundTotalPrice + outboundTotalAddOnPrice + outboundTotalExtrasPrice}
                outboundTotalPoints={outboundTotalPoints}
                inboundTotalPoints={inboundTotalPoints}
                inboundTotalPrice={inboundTotalPrice + inboundTotalAddOnPrice + inboundTotalExtrasPrice}
                totalPrice={totalPrice}
                isReturnJourney={!isEmpty(outbound) && !isEmpty(inbound)}
                passengers={passengers}
                currency={currency}
                offlineFee={offlineFee}
                originUIC={outboundOrigin?.uic}
                destinationUIC={outboundDestination?.uic}
                infantCount={infantCount}
                isLoyaltyBooking={isLoyalty}
                confirmButton={
                  <ConfirmButton
                    loading={loading || !meals.isDone}
                    disabled={isConfirmButtonDisabled || shouldBlockAddingAssistance}
                    type="submit"
                    data-cy="bp-continue"
                    variant="primary"
                  >
                    <FormattedMessage id="bookingPath.continue" />
                    <ChevronRight />
                  </ConfirmButton>
                }
                outboundExtras={nonCancelledOutboundExtras}
                inboundExtras={nonCancelledInboundExtras}
              />
            </form>
          </FormProvider>
        );
      }}
    </SeatSelection>
  );
};

BookingPathPassengerDetails.propTypes = {
  bookingData: bookingShape,
  passengerDetails: passengersShape,
  updatePassengers: PropTypes.func.isRequired,
  customerInfo: customerShape,
  canAddSpecialAssistanceToBooking: PropTypes.bool,
  isAssistanceSelected: PropTypes.bool,
  onIsAssistanceSelected: PropTypes.func,
  client: PropTypes.shape({
    writeQuery: PropTypes.func
  }),
  loading: PropTypes.bool,
  history: historyShape,
  handleCompletedMutation: PropTypes.func,
  infantsOnLapEnabled: PropTypes.bool,
  infantCount: PropTypes.number,
  addedParentIds: PropTypes.instanceOf(Set),
  existingInfants: PropTypes.arrayOf(PropTypes.shape({ passengerRef: PropTypes.string }))
};

export default BookingPathPassengerDetails;
